import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getRequest, postRequest, updateRequest, deleteRequest } from "../services";
import toast from "react-hot-toast";

const initialState = {
  management: [],
  permissions: [],
  loading: false,
  error: null,
};

const getErrorMessage = (error, fallback) =>
  error?.data?.message ||
  error?.data?.title ||
  (typeof error?.data === "string" ? error.data : null) ||
  error?.statusText ||
  error?.message ||
  fallback;

export const fetchPermissions = createAsyncThunk(
  "management/fetchPermissions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRequest("/api/Management/permissions");
      if (response.status !== 200) {
        return rejectWithValue("Failed to fetch permissions");
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch permissions"));
    }
  }
);

export const fetchManagement = createAsyncThunk(
  "management/fetchManagement",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRequest("/api/Management");
      if (response.status !== 200) {
        return rejectWithValue("Failed to fetch management data");
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch management data"));
    }
  }
);

export const addManagement = createAsyncThunk(
  "management/addManagement",
  async (management, { rejectWithValue }) => {
    try {
      const response = await postRequest("/api/Management/admin", management);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to add administrator"));
    }
  }
);

export const updateManagement = createAsyncThunk(
  "management/updateManagement",
  async ({ id, management }, { rejectWithValue }) => {
    try {
      const response = await updateRequest(`/api/Management/${id}`, management);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to update administrator"));
    }
  }
);

export const deleteManagement = createAsyncThunk(
  "management/deleteManagement",
  async (id, { rejectWithValue }) => {
    try {
      await deleteRequest(`/api/Management/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to delete administrator"));
    }
  }
);

const managementSlice = createSlice({
  name: "management",
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(fetchPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.permissions = action.payload;
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchManagement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchManagement.fulfilled, (state, action) => {
        state.loading = false;
        state.management = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchManagement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        toast.error(action.payload || "Failed to fetch administrators");
      })
      .addCase(addManagement.fulfilled, (state, action) => {
        if (action.payload?.id) {
          state.management = [...state.management, action.payload];
        }
        toast.success("Administrator added successfully");
      })
      .addCase(addManagement.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(action.payload || "Failed to add administrator");
      })
      .addCase(updateManagement.fulfilled, (state, action) => {
        const updatedId = String(action.payload?.id ?? "");
        const index = state.management.findIndex(
          (m) => String(m.id) === updatedId
        );
        if (index !== -1) {
          state.management[index] = {
            ...state.management[index],
            ...action.payload,
          };
        }
        toast.success("Administrator updated successfully");
      })
      .addCase(updateManagement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        toast.error(action.payload || "Failed to update administrator");
      })
      .addCase(deleteManagement.fulfilled, (state, action) => {
        const deletedId = String(action.payload);
        state.management = state.management.filter(
          (m) => String(m.id) !== deletedId
        );
        toast.success("Administrator deleted successfully");
      })
      .addCase(deleteManagement.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(action.payload || "Failed to delete administrator");
      });
  },
});

export default managementSlice.reducer;
